'use client'

import { useState } from 'react'
import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui/dialog'

interface AddTopicDialogProps {
  /** Controlled open state */
  open: boolean
  /** Called when open state changes */
  onOpenChange: (open: boolean) => void
  /** Called when user creates a new topic */
  onCreate: (name: string) => void
}

/**
 * A dialog for quickly adding a new topic.
 * Designed to be triggered from the topics multi-select.
 */
export function AddTopicDialog({
  open,
  onOpenChange,
  onCreate,
}: AddTopicDialogProps) {
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation() // Prevent event from bubbling to parent form
    if (!name.trim()) {
      setError('Topic name is required')
      return
    }

    setError(null)
    onCreate(name.trim())

    // Reset form
    setName('')
    onOpenChange(false)
  }

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen)
    if (!newOpen) {
      // Reset form when closing
      setName('')
      setError(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Add New Topic</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="topic-name" className="text-sm font-medium">
              Topic Name
            </label>
            <Input
              id="topic-name"
              placeholder="e.g., Personal Growth"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (error) setError(null)
              }}
              autoFocus
            />
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim()}>
              Create Topic
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
